/* eslint-disable no-undef */
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Labelbox, Button, Table, Modal } from "../../components";
import {
  Toaster,
  dateFormatter,
  dropdownNameWithEmail,
} from "../../utils/common";
import { LEAVE_ACTION, EMPLOYEE_ACTION } from "../../redux/action";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, TextField } from "@mui/material";
import * as FileSaver from "file-saver";
import { utils, write } from "xlsx";
import "./leaves.scss";
import { hasHrmsAdminAccess } from "../../utils/hrmsAccess";
import {
  approverEmailsForTeam,
  isTeamFirstApprover,
  normalizeEmployeeTeam,
  visibleTeamsForEmail,
} from "../../utils/leaveTeamApprovers";
import { canAccessWorkFromHome } from "../../utils/workFromHomeAccess";
import API from "../../utils/api";

const CASUAL_LEAVE_PER_YEAR = 12;

const LEAVE_BALANCE_CARD_ORDER = ["Casual", "Sick", "Comp Off"];

/** Custom leave days: positive whole numbers or .5 steps only (1, 1.5, 2, …). */
const isValidCustomLeaveDays = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return false;
  return Math.abs(n * 2 - Math.round(n * 2)) < 1e-8;
};

/** Allow empty / in-progress typing for the custom days field. */
const isAllowedCustomDaysInput = (raw) => {
  const s = String(raw ?? "").trim();
  if (s === "") return true;
  if (/^\d+\.?$/.test(s)) return true;
  if (/^\d+\.5$/.test(s)) return true;
  return false;
};

/** End date = start + ceil(customDays) weekdays (Sat/Sun skipped). */
const parseYmdToLocalDate = (ymd) => {
  const match = String(ymd || "")
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0,
    0,
    0,
    0,
  );
};

const formatLocalDateYmd = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const countWeekdaysInclusive = (start, end) => {
  if (!start || !end || end < start) return 0;
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    if (!isWeekend(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const addWeekdaysFromStart = (start, weekdayCount) => {
  if (!start || weekdayCount < 1) return null;
  const cursor = new Date(start);
  let remaining = weekdayCount;
  while (remaining > 0) {
    if (!isWeekend(cursor)) remaining -= 1;
    if (remaining > 0) cursor.setDate(cursor.getDate() + 1);
  }
  return cursor;
};

const getEndDateYmdFromCustomDays = (startYmd, customDays) => {
  if (!startYmd || !isValidCustomLeaveDays(customDays)) return "";
  const start = parseYmdToLocalDate(startYmd);
  if (!start) return "";
  const weekdaySpan = Math.ceil(Number(customDays));
  const end = addWeekdaysFromStart(start, weekdaySpan);
  return formatLocalDateYmd(end);
};

const normalizeEmployeePayload = (payload) => {
  if (payload == null) return {};
  if (Array.isArray(payload)) {
    const first = payload[0];
    return first && typeof first === "object" ? first : {};
  }
  if (typeof payload === "object") return payload;
  return {};
};

/** Legacy backend types that should display / count as Comp Off. */
const isLegacyOtherLeaveType = (leaveType) => {
  const key = String(leaveType || "")
    .trim()
    .toLowerCase();
  return key === "privilege" || key === "other";
};

const formatLeaveTypeDisplay = (leaveType) => {
  if (isLegacyOtherLeaveType(leaveType)) return "Comp Off";
  return String(leaveType || "").trim() || "N/A";
};

const normalizeLeaveTypeForCounting = (leaveType) => {
  if (isLegacyOtherLeaveType(leaveType)) return "Comp Off";
  return String(leaveType || "");
};

const isLopLeaveType = (leaveType) =>
  String(leaveType || "")
    .trim()
    .toLowerCase() === "lop";

const isUnlimitedBalanceLeaveType = (leaveType) => {
  const key = String(leaveType || "").trim();
  return key === "Work From Home" || key === "LOP";
};

/** Notice time limits are disabled. Leave can be applied for prior dates. */
/*
const getLeaveNoticeHours = (leaveType, isEmergency) => {
  if (isEmergency) return 0;
  if (isLopLeaveType(leaveType)) return 0;
  if (leaveType === "Casual" || leaveType === "Comp Off") {
    return 36;
  }
  if (leaveType === "Work From Home") return 1;
  return 0;
};

const isLeaveDateBlockedByNotice = (date, leaveType, isEmergency) => {
  if (!date) return false;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const requiredHours = getLeaveNoticeHours(leaveType, Boolean(isEmergency));
  const leaveStart = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    9,
    0,
    0,
    0,
  );
  const diffHours = (leaveStart.getTime() - now.getTime()) / (1000 * 60 * 60);
  return diffHours < requiredHours;
};
*/

const isEmergencyLeaveRecord = (leave) => {
  const raw = leave?.is_emergency ?? leave?.isEmergency ?? leave?.emergency;
  return raw === true || raw === 1 || String(raw).trim() === "1";
};

/** Emergency leave may only consume Comp Off (preferred) or Casual. */
const formatEmergencyLeaveTypeDisplay = (leaveType) => {
  const base = formatLeaveTypeDisplay(leaveType);
  if (base === "Comp Off" || base === "Casual") {
    return `${base} (Emergency)`;
  }
  return "Emergency";
};

const Leaves = ({
  defaultActiveTab,
  scopeEmployeeId = null,
  readOnlyView = false,
  hideApplyTab = false,
  hideFilters = false,
  hideStatistics = false,
} = {}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const hasHandledNotificationRef = useRef(false); // Track if we've already handled the notification
  const [activeTab, setActiveTab] = useState("list"); // "apply", "list", "balance"
  const [managerListView, setManagerListView] = useState("team"); // "team" | "my" (manager only)
  const [loading, setLoading] = useState(false);
  const [notificationFilterActive, setNotificationFilterActive] =
    useState(false); // Track if notification filter is active
  const [notificationLeaveId, setNotificationLeaveId] = useState(null); // Store notification leave_id in local state
  const [formData, setFormData] = useState({
    leave_type: "",
    day_type: "", // "full", "half", or "custom"
    custom_days: "",
    start_date: "",
    end_date: "",
    reason: "",
    notification_emails: "",
    is_emergency: false,
    status: 1, // 1 = Pending, 0 = Saved as Draft
  });
  const managerEmailRef = useRef("");
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    leave_type: "",
    status: "",
    employee_id: "all", // Admin only: filter leaves by employee - default to "all"
  });
  const [approveRejectModal, setApproveRejectModal] = useState({
    open: false,
    type: "", // "approve" or "reject"
    leaveId: null,
    leaveData: null,
  });
  const [rejectionComments, setRejectionComments] = useState("");
  const [viewReasonModal, setViewReasonModal] = useState({
    open: false,
    reason: "",
    rejectedReason: "",
    isRejection: false,
  });
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, leave: null });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    leaveId: null,
  });
  const [editingLeaveId, setEditingLeaveId] = useState(null);

  const [compOffModalOpen, setCompOffModalOpen] = useState(false);
  const [compOffDays, setCompOffDays] = useState("1");
  const [compOffDescription, setCompOffDescription] = useState("");
  const [compOffSelectedOptions, setCompOffSelectedOptions] = useState([]); // Store option objects directly

  const userRole = localStorage.getItem("user_role");
  const userId = localStorage.getItem("user_id");
  const userEmail = (() => {
    try {
      const stored = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
      return stored?.email || stored?.user_email || "";
    } catch (_error) {
      return "";
    }
  })();
  const teamLeaveScope = visibleTeamsForEmail(userEmail);
  const isAdmin = hasHrmsAdminAccess(userId);
  const isTeamLeaveApprover = !isAdmin && teamLeaveScope.length > 0;
  const isManager = userRole === "manager" && !isAdmin;
  const isEmployee = userRole === "employee";

  const scopedEmployeeId = useMemo(() => {
    const value =
      scopeEmployeeId === null || scopeEmployeeId === undefined
        ? ""
        : String(scopeEmployeeId).trim();
    return value;
  }, [scopeEmployeeId]);

  const isScopedEmployeeView = Boolean(scopedEmployeeId);
  const effectiveLeaveEmployeeId = scopedEmployeeId || userId || "";
  const canShowWorkFromHome = canAccessWorkFromHome(effectiveLeaveEmployeeId);
  const canApplyWorkFromHome = canAccessWorkFromHome(userId);

  useEffect(() => {
    if (defaultActiveTab) {
      setActiveTab(defaultActiveTab);
      return;
    }

    if (!isAdmin && !hideApplyTab && !isScopedEmployeeView) {
      setActiveTab("apply");
      return;
    }

    setActiveTab("list");
  }, [defaultActiveTab, isAdmin, hideApplyTab, isScopedEmployeeView]);

  useEffect(() => {
    if (!canApplyWorkFromHome && formData.leave_type === "Work From Home") {
      setFormData((prev) => ({
        ...prev,
        leave_type: "",
        start_date: "",
        end_date: "",
      }));
      setCalculatedDays(0);
    }
  }, [canApplyWorkFromHome, formData.leave_type]);

  // Prefill notification emails with the employee's manager email
  useEffect(() => {
    let cancelled = false;

    const loadManagerEmail = async () => {
      try {
        let managerEmail = "";
        let managerId = "";
        let team = "";

        try {
          const userDataRaw = localStorage.getItem("USER_DATA");
          const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
          managerEmail = String(
            userData?.manager_email ||
              userData?.managerEmail ||
              userData?.manager?.email ||
              userData?.reporting_manager_email ||
              "",
          ).trim();
          managerId = String(
            userData?.manager_id ||
              userData?.managerId ||
              userData?.manager?.employee_id ||
              userData?.manager?.id ||
              "",
          ).trim();
          team = String(userData?.team || "").trim();
        } catch (e) {}

        if (userId && (!managerEmail || !team || !managerId)) {
          const { data } = await API.get(
            `employee/getEmployeeById/${String(userId)}`,
          );
          const me = normalizeEmployeePayload(data?.data);
          if (!team) team = String(me?.team || "").trim();
          if (!managerId) {
            managerId = String(me?.manager_id || me?.managerId || "").trim();
          }
        }

        if (!managerEmail && managerId) {
          const { data } = await API.get(
            `employee/getEmployeeById/${String(managerId)}`,
          );
          const mgr = normalizeEmployeePayload(data?.data);
          managerEmail = String(mgr?.email || "").trim();
        }

        const notificationEmails = [];
        const seen = new Set();
        for (const raw of [managerEmail, ...approverEmailsForTeam(team)]) {
          const email = String(raw || "").trim();
          const key = email.toLowerCase();
          if (!email || seen.has(key)) continue;
          seen.add(key);
          notificationEmails.push(email);
        }

        if (cancelled || !notificationEmails.length) return;
        const joined = notificationEmails.join(", ");
        managerEmailRef.current = joined;
        setFormData((prev) => {
          const current = String(prev.notification_emails || "").trim();
          if (
            current &&
            current.toLowerCase() !== String(managerEmail || "").trim().toLowerCase()
          ) {
            return prev;
          }
          return { ...prev, notification_emails: joined };
        });
      } catch (e) {}
    };

    loadManagerEmail();
    return () => {
      cancelled = true;
    };
  }, [dispatch, userId]);

  const { leave } = useSelector((state) => state);
  const { employee } = useSelector((state) => state);
  const leaveBalance = leave?.leaveBalance || [];
  const allLeaves = leave?.allLeaves || [];
  const leaveStatistics = leave?.leaveStatistics || null;
  const activeEmployees = employee?.activeEmployees || [];

  const displayedLeaveBalance = useMemo(() => {
    const byType = new Map();
    (leaveBalance || []).forEach((b) => {
      const raw = String(b?.leave_type || "").trim();
      const leaveType = isLegacyOtherLeaveType(raw) ? "Comp Off" : raw;
      if (!leaveType) return;
      if (isLopLeaveType(leaveType) || leaveType === "Work From Home") return;
      const existing = byType.get(leaveType);
      if (!existing) {
        byType.set(leaveType, { ...b, leave_type: leaveType });
        return;
      }
      byType.set(leaveType, {
        ...existing,
        ...b,
        leave_type: leaveType,
        balance: Number(existing.balance || 0) + Number(b.balance || 0),
      });
    });
    const merged = Array.from(byType.values());
    const orderIndex = (leaveType) => {
      const key = String(leaveType || "").toLowerCase();
      const idx = LEAVE_BALANCE_CARD_ORDER.findIndex(
        (t) => t.toLowerCase() === key,
      );
      return idx === -1 ? LEAVE_BALANCE_CARD_ORDER.length : idx;
    };
    return merged.sort(
      (a, b) => orderIndex(a?.leave_type) - orderIndex(b?.leave_type),
    );
  }, [leaveBalance]);

  const leaveTypeOptions = useMemo(() => {
    const options = [];

    const pushIfAvailable = (id, displayValue) => {
      const bal = leaveBalance.find((b) => b.leave_type === id);
      if (bal && Number(bal.balance || 0) > 0) {
        options.push({ id, value: displayValue });
      }
    };

    pushIfAvailable("Casual", "Casual Leave");
    pushIfAvailable("Sick", "Sick Leave");

    // Comp Off always listed (replaces former "Other"); balance still enforced on submit
    options.push({ id: "Comp Off", value: "Comp Off" });

    // WFH only for allowlisted employees; one per month still enforced on submit
    if (canApplyWorkFromHome) {
      options.push({ id: "Work From Home", value: "Work From Home" });
    }

    // LOP always available regardless of balance
    options.push({ id: "LOP", value: "LOP" });

    // Emergency Leave only when Comp Off or Casual balance exists (Comp Off preferred on submit)
    const compOffBal = leaveBalance.find(
      (b) =>
        b.leave_type === "Comp Off" || isLegacyOtherLeaveType(b?.leave_type),
    );
    const casualBal = leaveBalance.find((b) => b.leave_type === "Casual");
    const hasEmergencySource =
      Number(compOffBal?.balance || 0) > 0 ||
      Number(casualBal?.balance || 0) > 0;
    if (hasEmergencySource) {
      options.push({ id: "Emergency Leave", value: "Emergency Leave" });
    }

    return options;
  }, [leaveBalance, canApplyWorkFromHome]);

  function getLeaveEmployeeId(leaveRow) {
    // Support multiple possible backend keys
    return (
      leaveRow?.employee_id ??
      leaveRow?.employeeId ??
      leaveRow?.emp_id ??
      leaveRow?.user_id ??
      null
    );
  }

  const parseLeaveDateOnly = (value) => {
    if (!value) return null;
    const s = String(value).trim();
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymd) {
      return new Date(
        Number(ymd[1]),
        Number(ymd[2]) - 1,
        Number(ymd[3]),
        0,
        0,
        0,
        0,
      );
    }
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  };

  /** Pending or approved WFH already counts as "taken" for the month. */
  const hasWorkFromHomeInMonth = useCallback(
    (year, monthIndex, excludeLeaveId = null) => {
      if (!Array.isArray(allLeaves) || year == null || monthIndex == null) {
        return false;
      }
      const monthStart = new Date(year, monthIndex, 1);
      const monthEnd = new Date(year, monthIndex + 1, 0);

      return allLeaves.some((leave) => {
        if (String(getLeaveEmployeeId(leave)) !== String(userId)) return false;
        if (String(leave?.leave_type) !== "Work From Home") return false;
        const status = String(leave?.status ?? "");
        if (status !== "1" && status !== "2") return false; // pending / approved
        if (
          excludeLeaveId != null &&
          String(leave?.leave_id ?? leave?.id) === String(excludeLeaveId)
        ) {
          return false;
        }
        const start = parseLeaveDateOnly(leave?.start_date);
        const end = parseLeaveDateOnly(leave?.end_date) || start;
        if (!start) return false;
        return start <= monthEnd && end >= monthStart;
      });
    },
    [allLeaves, userId],
  );

  const hasEmergencyLeaveInMonth = useCallback(
    (year, monthIndex, excludeLeaveId = null) => {
      if (!Array.isArray(allLeaves) || year == null || monthIndex == null) {
        return false;
      }
      const monthStart = new Date(year, monthIndex, 1);
      const monthEnd = new Date(year, monthIndex + 1, 0);

      return allLeaves.some((leave) => {
        if (String(getLeaveEmployeeId(leave)) !== String(userId)) return false;
        if (!isEmergencyLeaveRecord(leave)) return false;
        const status = String(leave?.status ?? "");
        if (status !== "1" && status !== "2") return false;
        if (
          excludeLeaveId != null &&
          String(leave?.leave_id ?? leave?.id) === String(excludeLeaveId)
        ) {
          return false;
        }
        const start = parseLeaveDateOnly(leave?.start_date);
        const end = parseLeaveDateOnly(leave?.end_date) || start;
        if (!start) return false;
        return start <= monthEnd && end >= monthStart;
      });
    },
    [allLeaves, userId],
  );

  const emergencyLeaveBalance = useMemo(() => {
    const now = new Date();
    const taken = hasEmergencyLeaveInMonth(now.getFullYear(), now.getMonth());
    return taken ? 0 : 1;
  }, [hasEmergencyLeaveInMonth]);

  const getEmergencyAlreadyTakenMessage = (dateValue) => {
    const d = parseLeaveDateOnly(dateValue) || new Date();
    const monthLabel = d.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    return `Emergency leave for ${monthLabel} is already applied`;
  };

  const getWfhAlreadyTakenMessage = (dateValue) => {
    const d = parseLeaveDateOnly(dateValue) || new Date();
    const monthLabel = d.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    });
    return `Work From Home for ${monthLabel} is already taken`;
  };

  const shouldDisableLeaveDate = useMemo(() => {
    const leaveType = String(formData.leave_type || "");
    const isEmergency =
      leaveType === "Emergency Leave" ||
      (Boolean(formData.is_emergency) && !isLopLeaveType(leaveType));
    return (date) => {
      if (!date) return false;
      const d = new Date(date);
      if (
        leaveType === "Work From Home" &&
        hasWorkFromHomeInMonth(d.getFullYear(), d.getMonth())
      ) {
        return true;
      }
      if (
        isEmergency &&
        hasEmergencyLeaveInMonth(d.getFullYear(), d.getMonth())
      ) {
        return true;
      }
      // Notice period and past dates are no longer blocked.
      // return isLeaveDateBlockedByNotice(d, leaveType, isEmergency);
      return false;
    };
  }, [
    formData.leave_type,
    formData.is_emergency,
    hasWorkFromHomeInMonth,
    hasEmergencyLeaveInMonth,
  ]);

  useEffect(() => {
    if (!formData.start_date) return;
    const start = parseLeaveDateOnly(formData.start_date);
    if (start && shouldDisableLeaveDate(start)) {
      setFormData((prev) => ({ ...prev, start_date: "", end_date: "" }));
      Toaster.warning(
        formData.is_emergency
          ? hasEmergencyLeaveInMonth(start.getFullYear(), start.getMonth())
            ? getEmergencyAlreadyTakenMessage(start)
            : "Emergency leave must be applied before 9:00 AM on the leave day. Please select a valid date."
          : "Selected date no longer meets the notice period. Please reselect dates.",
      );
    }
    // Only re-check when emergency is toggled; leave type already resets dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.is_emergency]);

  const wfhTakenDays = useMemo(() => {
    if (!allLeaves || !Array.isArray(allLeaves)) return 0;
    return allLeaves
      .filter(
        (l) =>
          String(getLeaveEmployeeId(l)) === String(effectiveLeaveEmployeeId) &&
          String(l?.leave_type) === "Work From Home" &&
          String(l?.status) === "2",
      )
      .reduce((sum, l) => sum + Number(l?.total_days || 0), 0);
  }, [allLeaves, effectiveLeaveEmployeeId]);

  const lopTakenDays = useMemo(() => {
    if (!allLeaves || !Array.isArray(allLeaves)) return 0;
    return allLeaves
      .filter(
        (l) =>
          String(getLeaveEmployeeId(l)) === String(effectiveLeaveEmployeeId) &&
          String(l?.leave_type) === "LOP" &&
          String(l?.status) === "2",
      )
      .reduce((sum, l) => sum + Number(l?.total_days || 0), 0);
  }, [allLeaves, effectiveLeaveEmployeeId]);

  // Effective days considering full / half-day / custom selection
  const effectiveDays = useMemo(() => {
    if (formData.day_type === "custom") {
      return isValidCustomLeaveDays(formData.custom_days)
        ? Number(formData.custom_days)
        : 0;
    }
    if (!calculatedDays || Number.isNaN(Number(calculatedDays))) return 0;
    if (formData.day_type === "half") {
      return calculatedDays * 0.5;
    }
    return calculatedDays;
  }, [calculatedDays, formData.day_type, formData.custom_days]);

  useEffect(() => {
    // Everyone can view leaves list (role-filtered by backend)
    dispatch(LEAVE_ACTION.getAllLeaves({}));
    // Needed for Apply Leave (admins can apply for themselves too)
    dispatch(
      LEAVE_ACTION.getLeaveBalance({ employee_id: effectiveLeaveEmployeeId }),
    );
    dispatch(LEAVE_ACTION.getLeaveStatistics());
  }, [dispatch, userId, effectiveLeaveEmployeeId]);

  // Admin comp-off + Admin/Manager leaves list filter need employee list
  useEffect(() => {
    if (isAdmin || isManager || isTeamLeaveApprover) {
      dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    }
  }, [dispatch, isAdmin, isManager, isTeamLeaveApprover]);

  const compOffEmployeeOptions = useMemo(() => {
    if (!Array.isArray(activeEmployees)) return [];
    // Build safe unique options (avoid NaN keys)
    const map = new Map(); // key: string employee_id -> option
    activeEmployees.forEach((e) => {
      const rawId =
        e?.employee_id ?? e?.emp_id ?? e?.user_id ?? e?.id ?? e?.employeeId;
      if (rawId === null || rawId === undefined) return;
      const idStr = String(rawId).trim();
      if (!idStr) return;
      if (map.has(idStr)) return;

      map.set(idStr, {
        value: idStr, // keep as string to avoid NaN
        label:
          dropdownNameWithEmail(e) ||
          `${e.first_name || ""} ${e.last_name || ""}`.trim() ||
          e.email ||
          idStr,
        firstName: (e.first_name || "").trim().toLowerCase(), // for sorting
      });
    });

    // Sort by first name alphabetically
    return Array.from(map.values()).sort((a, b) => {
      return a.firstName.localeCompare(b.firstName);
    });
  }, [activeEmployees]);

  // Get selected IDs for checkbox checking
  const compOffSelectedIdSet = useMemo(() => {
    return new Set(
      (compOffSelectedOptions || [])
        .map((o) => String(o?.value ?? "").trim())
        .filter(Boolean),
    );
  }, [compOffSelectedOptions]);

  const compOffSelectedLabels = useMemo(
    () => (compOffSelectedOptions || []).map((o) => o?.label).filter(Boolean),
    [compOffSelectedOptions],
  );

  // Admin and Manager (Team Leaves): employee options for leaves list filter
  const leaveListEmployeeOptions = useMemo(() => {
    if (
      (!isAdmin && !isManager && !isTeamLeaveApprover) ||
      !Array.isArray(activeEmployees)
    ) {
      return [];
    }
    const allOption = { id: "all", value: "All Employees", keepAtTop: true };
    const empOptions = activeEmployees
      .filter((employeeRow) => {
        if (!isTeamLeaveApprover || isAdmin) return true;
        const onTeam = teamLeaveScope.includes(
          normalizeEmployeeTeam(employeeRow?.team),
        );
        const sameId = (left, right) =>
          String(left || "").trim().toLowerCase() ===
          String(right || "").trim().toLowerCase();
        const directReport =
          isManager &&
          sameId(employeeRow?.manager_id, userId) &&
          !sameId(employeeRow?.employee_id || employeeRow?.id, userId);
        return onTeam || directReport;
      })
      .map((e) => {
      const rawId =
        e?.employee_id ?? e?.emp_id ?? e?.user_id ?? e?.id ?? e?.employeeId;
      const idStr = rawId != null ? String(rawId) : "";
      const label =
        dropdownNameWithEmail(e) ||
        `${e.first_name || ""} ${e.last_name || ""}`.trim() ||
        e.email ||
        idStr;
      return { id: idStr, value: label };
    });
    return [allOption, ...empOptions];
  }, [isAdmin, isManager, isTeamLeaveApprover, teamLeaveScope, activeEmployees, userId]);

  // When coming from notification, switch to list tab and show filtered leave
  useEffect(() => {
    const isFromNotification = !!location.state?.filterByLeaveId;

    if (isFromNotification && !hasHandledNotificationRef.current) {
      // Store leave_id in local state before clearing location state
      setNotificationLeaveId(location.state.leave_id);
      setNotificationFilterActive(true);
      setActiveTab("list");
      hasHandledNotificationRef.current = true; // Mark as handled
      // Clear the location state to prevent filter from persisting on refresh
      navigate(location.pathname, { replace: true, state: null });
    } else if (!isFromNotification && !hasHandledNotificationRef.current) {
      // Normal page load without notification - ensure filter is off
      setNotificationFilterActive(false);
      setNotificationLeaveId(null);
    }
  }, [location.state?.filterByLeaveId, navigate, location.pathname]); // Re-run when notification state changes

  // Reset manager list view when role changes
  useEffect(() => {
    if (!isManager) return;
    if (managerListView !== "team" && managerListView !== "my") {
      setManagerListView("team");
    }
  }, [isManager, managerListView]);

  // Clear employee filter when manager switches to "My Leaves"
  useEffect(() => {
    if (
      isManager &&
      managerListView === "my" &&
      filters.employee_id !== "all"
    ) {
      setFilters((prev) => ({ ...prev, employee_id: "all" }));
    }
  }, [isManager, managerListView]);

  // Clear notification filter when user manually changes tab or filters
  useEffect(() => {
    if (notificationFilterActive && activeTab !== "list") {
      setNotificationFilterActive(false);
      setNotificationLeaveId(null);
    }
  }, [activeTab, notificationFilterActive]);

  // Clear notification filter when user manually changes any filter
  useEffect(() => {
    if (
      notificationFilterActive &&
      (filters.start_date ||
        filters.end_date ||
        filters.leave_type ||
        filters.status) &&
      filters.employee_id !== "all"
    ) {
      setNotificationFilterActive(false);
      setNotificationLeaveId(null);
    }
  }, [filters, notificationFilterActive]);

  // Reset end date when leave type changes to ensure proper validation
  useEffect(() => {
    if (formData.end_date && formData.start_date) {
      // Re-validate when leave type changes - parse dates correctly
      const startParts = formData.start_date.split("-");
      const endParts = formData.end_date.split("-");
      const start = new Date(
        parseInt(startParts[0], 10),
        parseInt(startParts[1], 10) - 1,
        parseInt(startParts[2], 10),
      );
      const end = new Date(
        parseInt(endParts[0], 10),
        parseInt(endParts[1], 10) - 1,
        parseInt(endParts[2], 10),
      );
      const days = countWeekdaysInclusive(start, end);

      if (
        formData.leave_type !== "Work From Home" &&
        formData.leave_type !== "LOP"
      ) {
        // Custom duration drives its own end date; avoid interfering here.
        if (formData.day_type === "custom") return;
        const available = getAvailableBalance(formData.leave_type);
        const effectiveDaysNeeded =
          formData.day_type === "half" ? days * 0.5 : days;
        if (
          available !== "Unlimited" &&
          Number(available) > 0 &&
          effectiveDaysNeeded > Number(available) + 1e-8
        ) {
          // Reset end date if it exceeds new leave type balance
          setFormData((prev) => ({ ...prev, end_date: "" }));
          Toaster.warning(
            `Selected dates exceed available ${formData.leave_type} leave balance. Please reselect dates.`,
          );
        }
      }
    }
  }, [formData.leave_type]);

  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      // Parse dates correctly to avoid timezone issues
      const startParts = formData.start_date.split("-");
      const endParts = formData.end_date.split("-");
      const start = new Date(
        parseInt(startParts[0], 10),
        parseInt(startParts[1], 10) - 1,
        parseInt(startParts[2], 10),
      );
      const end = new Date(
        parseInt(endParts[0], 10),
        parseInt(endParts[1], 10) - 1,
        parseInt(endParts[2], 10),
      );
      const days = countWeekdaysInclusive(start, end);
      const validDays = days > 0 ? days : 0;
      setCalculatedDays(validDays);

      // Custom duration drives end date from custom days — don't shrink by calendar span.
      if (formData.day_type === "custom") {
        return;
      }

      // Validate against available balance (except WFH and LOP)
      if (!isUnlimitedBalanceLeaveType(formData.leave_type) && validDays > 0) {
        const available = getAvailableBalance(formData.leave_type);
        if (
          available !== "Unlimited" &&
          available > 0 &&
          (formData.day_type === "half" ? validDays * 0.5 : validDays) >
            Number(available) + 1e-8
        ) {
          // Auto-adjust end date to maximum allowed (calendar-day span),
          // taking half-day selection into account.
          const availableNum = Number(available);
          const maxCalendarDays =
            formData.day_type === "half"
              ? Math.floor((availableNum + 1e-9) / 0.5)
              : Math.floor(availableNum + 1e-9);

          if (maxCalendarDays < 1) {
            setFormData((prev) => ({ ...prev, end_date: "" }));
            return;
          }

          const maxEndDate = addWeekdaysFromStart(start, maxCalendarDays);
          const maxEndDateStr = formatLocalDateYmd(maxEndDate);
          if (maxEndDateStr && formData.end_date !== maxEndDateStr) {
            setFormData((prev) => ({ ...prev, end_date: maxEndDateStr }));
            Toaster.warning(
              `You can only select up to ${available} days. End date adjusted to ${maxEndDate.toLocaleDateString()}`,
            );
          }
        }
      }
    } else {
      setCalculatedDays(0);
    }
  }, [
    formData.start_date,
    formData.end_date,
    formData.leave_type,
    formData.day_type,
  ]);

  // Custom leave: auto-select end date from start date + custom days
  useEffect(() => {
    if (formData.day_type !== "custom") return;
    if (!formData.start_date || !isValidCustomLeaveDays(formData.custom_days)) {
      return;
    }
    const nextEnd = getEndDateYmdFromCustomDays(
      formData.start_date,
      formData.custom_days,
    );
    if (nextEnd && nextEnd !== formData.end_date) {
      setFormData((prev) => ({ ...prev, end_date: nextEnd }));
    }
  }, [
    formData.day_type,
    formData.start_date,
    formData.custom_days,
    formData.end_date,
  ]);

  const getLeaveBalanceNumber = (leaveType) => {
    if (isUnlimitedBalanceLeaveType(leaveType)) return Number.POSITIVE_INFINITY;
    let balance = leaveBalance.find((b) => b.leave_type === leaveType);
    if (!balance && leaveType === "Comp Off") {
      balance = leaveBalance.find((b) => isLegacyOtherLeaveType(b?.leave_type));
    }
    return balance ? Number(balance.balance || 0) : 0;
  };

  /**
   * Emergency leave deduction order: Comp Off first if available, else Casual.
   * No other leave types may be used for emergency.
   */
  const resolveEmergencyLeaveType = (daysNeeded = 1) => {
    const need = Number(daysNeeded);
    const required = Number.isFinite(need) && need > 0 ? need : 1;
    const compOff = getLeaveBalanceNumber("Comp Off");
    if (compOff >= required - 1e-8) return "Comp Off";
    const casual = getLeaveBalanceNumber("Casual");
    if (casual >= required - 1e-8) return "Casual";
    if (compOff > 0) return "Comp Off";
    if (casual > 0) return "Casual";
    return null;
  };

  const getAvailableBalance = (leaveType) => {
    if (leaveType === "Emergency Leave") {
      const source = resolveEmergencyLeaveType(effectiveDays || 1);
      return source ? getLeaveBalanceNumber(source) : 0;
    }
    if (isUnlimitedBalanceLeaveType(leaveType)) {
      return "Unlimited";
    }
    return getLeaveBalanceNumber(leaveType);
  };

  // Calculate maximum end date based on available leave balance
  const getMaxEndDate = () => {
    if (isUnlimitedBalanceLeaveType(formData.leave_type)) {
      return null; // No allocated balance for WFH or LOP
    }
    if (!formData.start_date) {
      return null;
    }
    const available = getAvailableBalance(formData.leave_type);
    if (available === "Unlimited" || available === 0) {
      return null;
    }
    const start = parseYmdToLocalDate(formData.start_date);
    if (!start) {
      return null;
    }

    const availableNum = Number(available);
    const maxCalendarDays =
      formData.day_type === "half"
        ? Math.floor((availableNum + 1e-9) / 0.5)
        : Math.floor(availableNum + 1e-9);

    // Keep at least the same-day selection possible for 0.5 balances.
    if (maxCalendarDays < 1) {
      return formData.start_date;
    }

    const maxEndDate = addWeekdaysFromStart(start, maxCalendarDays);
    return formatLocalDateYmd(maxEndDate);
  };

  const handleFormChange = (field, value) => {
    if (field === "day_type") {
      setFormData((prev) => ({
        ...prev,
        day_type: value,
        custom_days: value === "custom" ? prev.custom_days : "",
        start_date: "",
        end_date: "",
      }));
      setCalculatedDays(0);
      return;
    }
    if (field === "custom_days") {
      if (!isAllowedCustomDaysInput(value)) {
        Toaster.warning(
          "Custom days must be a whole number or .5 only (e.g. 1 or 1.5)",
        );
        return;
      }
      if (formData.is_emergency) {
        const num = Number(value);
        if (num > 1) {
          Toaster.warning("Emergency leave cannot exceed 1 day.");
          return;
        }
      }
      setFormData((prev) => ({
        ...prev,
        custom_days: value,
        start_date: "",
        end_date: "",
      }));
      setCalculatedDays(0);
      return;
    }
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (isDraft = false, e) => {
    if (e) {
      e.preventDefault();
    }

    if (!isDraft) {
      // Validate required fields for submission
      if (!formData.day_type) {
        Toaster.error("Please select Full Day, Half Day, or Custom");
        return;
      }

      if (formData.day_type === "custom") {
        if (!isValidCustomLeaveDays(formData.custom_days)) {
          Toaster.error(
            "Custom days must be a positive whole number or .5 only (e.g. 1 or 1.5)",
          );
          return;
        }
      }

      if (!formData.start_date || !formData.end_date) {
        Toaster.error("Please select start and end dates");
        return;
      }

      if (new Date(formData.end_date) < new Date(formData.start_date)) {
        Toaster.error("End date must be after start date");
        return;
      }

      if (!formData.reason.trim()) {
        Toaster.error("Please provide a reason");
        return;
      }

      if (isLopLeaveType(formData.leave_type) && formData.is_emergency) {
        Toaster.error("LOP cannot be applied as emergency leave.");
        return;
      }

      const isEmergencyApplication =
        formData.leave_type === "Emergency Leave" ||
        Boolean(formData.is_emergency);

      let emergencySourceType = null;
      if (isEmergencyApplication) {
        emergencySourceType = resolveEmergencyLeaveType(effectiveDays || 1);
        if (!emergencySourceType) {
          Toaster.error(
            "Emergency leave requires available Comp Off or Casual leave balance.",
          );
          return;
        }
      }

      // Notice time limit is disabled, including for prior dates.
      /*
      const startForNotice = parseLeaveDateOnly(formData.start_date);
      if (
        startForNotice &&
        isLeaveDateBlockedByNotice(
          startForNotice,
          isEmergencyApplication
            ? emergencySourceType || formData.leave_type
            : formData.leave_type,
          isEmergencyApplication,
        )
      ) {
        if (isEmergencyApplication) {
          Toaster.error(
            "Emergency leave must be applied before 9:00 AM on the leave start date.",
          );
        } else {
          const hours = getLeaveNoticeHours(formData.leave_type, false);
          Toaster.error(
            hours > 0
              ? `${formData.leave_type || "This"} leave must be applied at least ${hours} hours before 9:00 AM on the start date.`
              : "This leave must be applied before 9:00 AM on the start date.",
          );
        }
        return;
      }
      */

      // Work From Home: allowlisted only; once per calendar month; single day
      if (formData.leave_type === "Work From Home") {
        if (!canApplyWorkFromHome) {
          Toaster.error("Work From Home is not available for your account.");
          return;
        }
        const start = parseLeaveDateOnly(formData.start_date);
        const end = parseLeaveDateOnly(formData.end_date);
        if (
          start &&
          end &&
          (start.getFullYear() !== end.getFullYear() ||
            start.getMonth() !== end.getMonth() ||
            start.getDate() !== end.getDate())
        ) {
          Toaster.error("Work From Home can only be applied for a single day");
          return;
        }
        if (
          start &&
          hasWorkFromHomeInMonth(
            start.getFullYear(),
            start.getMonth(),
            editingLeaveId,
          )
        ) {
          Toaster.warning(getWfhAlreadyTakenMessage(start));
          return;
        }
      }

      if (isEmergencyApplication) {
        const start = parseLeaveDateOnly(formData.start_date);
        const end = parseLeaveDateOnly(formData.end_date);
        if (
          start &&
          end &&
          (start.getFullYear() !== end.getFullYear() ||
            start.getMonth() !== end.getMonth() ||
            start.getDate() !== end.getDate())
        ) {
          Toaster.error("Emergency leave can only be applied for a single day");
          return;
        }
        if (
          start &&
          hasEmergencyLeaveInMonth(
            start.getFullYear(),
            start.getMonth(),
            editingLeaveId,
          )
        ) {
          Toaster.warning(getEmergencyAlreadyTakenMessage(start));
          return;
        }
      }

      // Validate balance (except WFH and LOP) - use effective days (0.5 for half-day)
      if (!isUnlimitedBalanceLeaveType(formData.leave_type) || isEmergencyApplication) {
        const balanceLeaveType = isEmergencyApplication
          ? emergencySourceType
          : formData.leave_type;
        if (!isUnlimitedBalanceLeaveType(balanceLeaveType)) {
          let available = getAvailableBalance(balanceLeaveType);
          if (editingLeaveId && available !== "Unlimited") {
            const originalLeave = (allLeaves || []).find(
              (leave) =>
                String(leave?.leave_id || leave?.id) === String(editingLeaveId),
            );
            if (
              originalLeave &&
              String(originalLeave.leave_type) === String(balanceLeaveType) &&
              String(originalLeave.status) === "1"
            ) {
              available =
                Number(available) + (Number(originalLeave.total_days) || 0);
            }
          }
          if (available !== "Unlimited" && available < effectiveDays) {
            Toaster.error(
              isEmergencyApplication
                ? `Insufficient ${balanceLeaveType} balance for emergency leave. Available: ${available}, Required: ${effectiveDays}`
                : `Insufficient ${balanceLeaveType} balance. Available: ${available}, Required: ${effectiveDays}`,
            );
            return;
          }
        }
      }
    } else {
      // For drafts, at least leave_type and reason should be provided
      if (!formData.leave_type) {
        Toaster.error("Please select a leave type");
        return;
      }
    }

    setLoading(true);
    try {
      const isEmergencyApplication =
        formData.leave_type === "Emergency Leave" ||
        Boolean(formData.is_emergency);
      const emergencySourceType = isEmergencyApplication
        ? resolveEmergencyLeaveType(effectiveDays || 1)
        : null;

      // Build payload - only include dates if they exist and are not empty strings
      const payload = {
        employee_id: userId,
        leave_type: isEmergencyApplication
          ? emergencySourceType || "Casual"
          : formData.leave_type,
        day_type: formData.day_type || "",
        total_days: effectiveDays || 0,
        reason: formData.reason || "",
        notification_emails: formData.notification_emails || "",
        is_emergency: isEmergencyApplication ? 1 : 0,
        status: isDraft ? 0 : 1,
      };

      // Only add dates if they have values (for drafts, dates might be optional)
      if (formData.start_date && formData.start_date.trim() !== "") {
        payload.start_date = formData.start_date;
      }
      if (formData.end_date && formData.end_date.trim() !== "") {
        payload.end_date = formData.end_date;
      }

      const response = editingLeaveId
        ? await LEAVE_ACTION.updateLeave({
            ...payload,
            leave_id: editingLeaveId,
          })
        : await dispatch(LEAVE_ACTION.createLeave(payload));
      const result = editingLeaveId ? response : response?.payload;
      const saved =
        editingLeaveId ? result?.status === true : result?.status === 1;

      if (result) {
        if (saved) {
          Toaster.success(
            editingLeaveId
              ? "Leave updated successfully"
              : isDraft
                ? "Leave saved as draft successfully"
                : "Leave application submitted successfully",
          );
          setEditingLeaveId(null);
          // Reset form
          setFormData({
            leave_type: "",
            day_type: "",
            custom_days: "",
            start_date: "",
            end_date: "",
            reason: "",
            notification_emails: managerEmailRef.current || "",
            is_emergency: false,
            status: 1,
          });
          setCalculatedDays(0);
          // Refresh data
          dispatch(LEAVE_ACTION.getAllLeaves({}));
          dispatch(LEAVE_ACTION.getLeaveBalance({ employee_id: userId }));
          dispatch(LEAVE_ACTION.getLeaveStatistics());
        } else {
          // Show validation errors if any
          const errorMessage =
            result?.message || "Error submitting leave application";
          const errors = result?.data?.errors;
          if (errors) {
            const errorList = Object.entries(errors)
              .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
              .join("\n");
            Toaster.error(errorList || errorMessage);
          } else {
            Toaster.error(errorMessage);
          }
        }
      } else {
        Toaster.error("Error submitting leave application");
      }
    } catch (error) {
      Toaster.error("Error submitting leave application");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  useEffect(() => {
    // Filter out "all" before sending to backend - backend expects empty string for all employees
    const backendFilters = {
      ...filters,
      employee_id: filters.employee_id === "all" ? "" : filters.employee_id,
    };
    dispatch(LEAVE_ACTION.getAllLeaves(backendFilters));
  }, [filters, dispatch]);

  const handleApprove = async () => {
    if (!approveRejectModal.leaveId) return;

    setLoading(true);
    try {
      const response = await LEAVE_ACTION.approveLeave(
        approveRejectModal.leaveId,
      );
      if (response.status) {
        Toaster.success(response.message || "Leave approved successfully");
        setApproveRejectModal({
          open: false,
          type: "",
          leaveId: null,
          leaveData: null,
        });
        dispatch(LEAVE_ACTION.getAllLeaves(filters));
        dispatch(LEAVE_ACTION.getLeaveBalance({ employee_id: userId }));
        dispatch(LEAVE_ACTION.getLeaveStatistics());
      } else {
        Toaster.error(response.message || "Error approving leave");
      }
    } catch (error) {
      Toaster.error("Error approving leave");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!approveRejectModal.leaveId) return;
    if (!rejectionComments.trim()) {
      Toaster.error("Please provide rejection reason");
      return;
    }

    setLoading(true);
    try {
      const response = await LEAVE_ACTION.rejectLeave({
        leave_id: approveRejectModal.leaveId,
        rejected_comments: rejectionComments,
      });
      if (response.status) {
        Toaster.success(response.message || "Leave rejected successfully");
        setApproveRejectModal({
          open: false,
          type: "",
          leaveId: null,
          leaveData: null,
        });
        setRejectionComments("");
        dispatch(LEAVE_ACTION.getAllLeaves(filters));
        dispatch(LEAVE_ACTION.getLeaveBalance({ employee_id: userId }));
        dispatch(LEAVE_ACTION.getLeaveStatistics());
      } else {
        Toaster.error(response.message || "Error rejecting leave");
      }
    } catch (error) {
      Toaster.error("Error rejecting leave");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leaveId) => {
    if (!leaveId) return;
    setLoading(true);
    try {
      const response = await LEAVE_ACTION.deleteLeave(leaveId);
      if (response.status) {
        Toaster.success(response.message || "Leave deleted successfully");
        setDeleteModal({ open: false, leaveId: null });
        dispatch(LEAVE_ACTION.getAllLeaves(filters));
        dispatch(LEAVE_ACTION.getLeaveBalance({ employee_id: userId }));
      } else {
        Toaster.error(response.message || "Error deleting leave");
      }
    } catch (error) {
      Toaster.error("Error deleting leave");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (type, leave) => {
    setApproveRejectModal({
      open: true,
      type,
      leaveId: leave?.leave_id,
      leaveData: leave,
    });
  };

  const handleRowMenuOpen = (event, leave) => {
    setRowMenu({ anchorEl: event.currentTarget, leave });
  };

  const handleRowMenuClose = () => {
    setRowMenu({ anchorEl: null, leave: null });
  };

  const openCompOffModal = () => {
    setCompOffModalOpen(true);
  };

  const closeCompOffModal = () => {
    setCompOffModalOpen(false);
    setCompOffDays("1");
    setCompOffDescription("");
    setCompOffSelectedOptions([]);
  };

  const submitCompOff = async () => {
    const employeeIds = (compOffSelectedOptions || [])
      .map((o) => String(o?.value ?? "").trim())
      .filter(Boolean);

    if (employeeIds.length === 0) {
      Toaster.error("Please select at least one employee");
      return;
    }
    const days = Number(compOffDays);
    if (!Number.isFinite(days) || days <= 0) {
      Toaster.error("Please enter valid comp off days");
      return;
    }
    if (!compOffDescription || !compOffDescription.trim()) {
      Toaster.error("Please enter a description");
      return;
    }

    setLoading(true);
    try {
      const res = await dispatch(
        LEAVE_ACTION.addCompOff({
          employee_ids: employeeIds,
          days,
          description: compOffDescription.trim(),
        }),
      );

      const payload = res?.payload;
      if (payload && (payload.status === 1 || payload.success === true)) {
        Toaster.success(payload.message || "Comp off added successfully");
        closeCompOffModal();
        // Optional: refresh list, in case backend also logs an entry
        dispatch(LEAVE_ACTION.getAllLeaves(filters));
      } else {
        Toaster.error(payload?.message || "Failed to add comp off");
      }
    } catch (e) {
      Toaster.error("Failed to add comp off");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const sameUserId = (left, right) =>
    String(left || "").trim().toLowerCase() ===
    String(right || "").trim().toLowerCase();

  const hasApprovalId = (value) => String(value || "").trim() !== "";

  const canGiveManagerApproval = (leave) =>
    String(leave?.status) === "1" &&
    !sameUserId(leave?.employee_id, userId) &&
    !hasApprovalId(leave?.manager_approved_by) &&
    (sameUserId(leave?.manager_id, userId) ||
      (isTeamLeaveApprover &&
        isTeamFirstApprover(userEmail, leave?.employee_team)));

  const canGiveAdminApproval = (leave) =>
    String(leave?.status) === "1" &&
    isAdmin &&
    !sameUserId(leave?.employee_id, userId) &&
    hasApprovalId(leave?.manager_approved_by) &&
    !hasApprovalId(leave?.admin_approved_by) &&
    !sameUserId(leave?.manager_approved_by, userId);

  const canRejectLeave = (leave) =>
    String(leave?.status) === "1" &&
    !sameUserId(leave?.employee_id, userId) &&
    (isAdmin ||
      sameUserId(leave?.manager_id, userId) ||
      (isTeamLeaveApprover &&
        isTeamFirstApprover(userEmail, leave?.employee_team)));

  const getStatusBadge = (leaveOrStatus) => {
    const leave =
      leaveOrStatus && typeof leaveOrStatus === "object" ? leaveOrStatus : null;
    const status = leave ? leave.status : leaveOrStatus;
    const statusKey = String(status ?? "");
    let statusInfo = {
      1: { text: "Pending", class: "badge-orange" },
      2: { text: "Approved", class: "badge-green" },
      3: { text: "Rejected", class: "badge-red" },
    }[statusKey] || { text: "Pending", class: "badge-orange" };

    if (statusKey === "1" && leave) {
      const managerDone = hasApprovalId(leave.manager_approved_by);
      const adminDone = hasApprovalId(leave.admin_approved_by);
      if (managerDone && !adminDone) {
        statusInfo = {
          text: "1 of 2 - Manager approved, HR pending",
          class: "badge-orange",
        };
      } else if (adminDone && !managerDone) {
        statusInfo = {
          text: "1 of 2 - HR approved, manager pending",
          class: "badge-orange",
        };
      } else if (!managerDone && !adminDone) {
        statusInfo = {
          text: "0 of 2 - Manager and HR pending",
          class: "badge-orange",
        };
      }
    }

    return (
      <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>
    );
  };

  const getStatusText = (status) => {
    const statusMap = {
      1: "Pending",
      2: "Approved",
      3: "Rejected",
    };
    return statusMap[status] ?? "Pending";
  };

  // Available Leave = sick + casual + comp_off (no privilege; resets each calendar year)
  const getAvailableLeaveDisplay = (leave) => {
    const sick =
      Number(leave.available_sick_leave) ||
      Number(leave.sick_balance) ||
      Number(leave.sick_available) ||
      Number(leave.sickBalance) ||
      0;
    const casualAvailable =
      leave.available_casual_leave !== undefined &&
      leave.available_casual_leave !== null
        ? Number(leave.available_casual_leave) || 0
        : Math.max(
            0,
            CASUAL_LEAVE_PER_YEAR - (Number(leave.casual_leaves_taken) || 0),
          );
    const compoff = Number(leave.comp_off_available) || 0;
    return sick + casualAvailable + compoff;
  };

  const formatAvailableLeaveCell = (leave) => {
    const type = String(leave?.leave_type || "").trim();
    if (isLopLeaveType(type) || type === "Work From Home") return "—";
    return getAvailableLeaveDisplay(leave);
  };

  // Admin/Manager (when employee selected): export only approved leaves to Excel
  const handleExportLeavesToExcel = () => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return;
    const approvedLeaves = visibleLeaves.filter(
      (leave) => String(leave.status) === "2",
    );
    const exportData = approvedLeaves.map((leave) => ({
      Name: leave.employee_name || "N/A",
      "Applied On": dateFormatter(leave.createdAt),
      "Leave Type": formatLeaveTypeDisplay(leave.leave_type),
      "Start Date": dateFormatter(leave.start_date),
      "End Date": dateFormatter(leave.end_date),
      Days: leave.total_days ?? "",
      Status: getStatusText(leave.status),
    }));
    const headers = [
      "Name",
      "Applied On",
      "Leave Type",
      "Start Date",
      "End Date",
      "Days",
      "Status",
    ];
    const ws = utils.aoa_to_sheet([headers]);
    utils.sheet_add_json(ws, exportData, { origin: "A2", skipHeader: true });

    // Summary: Available Leaves and Leaves Taken (Sick + Casual + Comp Off, exclude WFH)
    const leaveTypesCounted = ["Sick", "Casual", "Comp Off", "LOP"];
    const leavesTakenTotal = approvedLeaves
      .filter((l) =>
        leaveTypesCounted.includes(
          normalizeLeaveTypeForCounting(l?.leave_type),
        ),
      )
      .reduce((sum, l) => sum + Number(l?.total_days || 0), 0);
    const seenEmployeeIds = new Set();
    let availableLeavesTotal = 0;
    approvedLeaves.forEach((leave) => {
      const eid = String(getLeaveEmployeeId(leave) ?? "");
      if (eid && !seenEmployeeIds.has(eid)) {
        seenEmployeeIds.add(eid);
        availableLeavesTotal += getAvailableLeaveDisplay(leave);
      }
    });
    // Format with . as decimal and " days" suffix (avoid locale comma)
    const availableStr = `${Number(availableLeavesTotal).toFixed(1)} days`;
    const takenStr = `${Number(leavesTakenTotal).toFixed(1)} days`;
    utils.sheet_add_aoa(
      ws,
      [
        ["Available Leaves", availableStr],
        ["Leaves Taken", takenStr],
      ],
      { origin: -1 },
    );

    const wb = {
      Sheets: { Leaves: ws },
      SheetNames: ["Leaves"],
    };
    const excelBuffer = write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });
    const employeeName = approvedLeaves[0]?.employee_name || "Leaves";
    const safeName = (employeeName || "Leaves")
      .replace(/\s+/g, "_")
      .replace(/[<>:"/\\|?*]/g, "");
    const fileName = `${safeName}_leave_data.xlsx`;
    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName,
    );
    Toaster.success("Leaves exported successfully");
  };

  const isSelfLeave = (leaveRow) => {
    const eid = getLeaveEmployeeId(leaveRow);
    if (eid === null || eid === undefined) return false;
    return String(eid) === String(userId);
  };

  const leaveHasAnyApproval = (leave) =>
    String(leave?.manager_approved_by || "").trim() !== "" ||
    String(leave?.admin_approved_by || "").trim() !== "";

  const canEditOrDeleteOwnLeave = (leave) => {
    if (!leave || !isSelfLeave(leave)) return false;
    const status = String(leave.status ?? "");
    if (status !== "0" && status !== "1") return false;
    return !leaveHasAnyApproval(leave);
  };

  const openLeaveEditor = (leave) => {
    if (!canEditOrDeleteOwnLeave(leave)) return;
    const start = String(leave.start_date || "").slice(0, 10);
    const end = String(leave.end_date || "").slice(0, 10);
    const dayTypeRaw = String(leave.day_type || "").toLowerCase();
    const dayType =
      dayTypeRaw === "full" || dayTypeRaw === "half" ? dayTypeRaw : "custom";
    setEditingLeaveId(leave.leave_id || leave.id);
    setFormData({
      leave_type: leave.is_emergency
        ? "Emergency Leave"
        : leave.leave_type || "",
      day_type: dayType,
      custom_days: dayType === "custom" ? String(leave.total_days ?? "") : "",
      start_date: start,
      end_date: end,
      reason: leave.reason || "",
      notification_emails:
        leave.notification_emails || managerEmailRef.current || "",
      is_emergency: Boolean(leave.is_emergency),
      status: Number(leave.status) || 1,
    });
    setCalculatedDays(Number(leave.total_days) || 0);
    setActiveTab("apply");
  };

  const visibleLeaves = useMemo(() => {
    if (!allLeaves || !Array.isArray(allLeaves)) return [];

    if (scopedEmployeeId) {
      return allLeaves.filter(
        (l) => String(getLeaveEmployeeId(l)) === scopedEmployeeId,
      );
    }

    // If notification filter is active, show only that specific leave
    if (notificationFilterActive && notificationLeaveId) {
      const notificationLeaveIdStr = String(notificationLeaveId).trim();
      console.log("Filtering leaves by notification:", {
        notificationLeaveId,
        notificationLeaveIdStr,
        totalLeaves: allLeaves.length,
        leaveIds: allLeaves.map((l) => ({
          leave_id: l.leave_id,
          id: l.id,
          leave_type: l.leave_type,
          employee_name: l.employee_name,
        })),
      });

      const filtered = allLeaves.filter((l) => {
        const leaveId = String(l.leave_id || l.id || "").trim();
        return leaveId === notificationLeaveIdStr;
      });

      // If no match found, log issue and return the first leave as fallback
      if (filtered.length === 0) {
        console.warn(
          "Leave with ID not found:",
          notificationLeaveIdStr,
          "Available leaves:",
          allLeaves.map((l) => ({
            id: l.leave_id || l.id,
            leave_type: l.leave_type,
          })),
        );
        return allLeaves.length > 0 ? [allLeaves[0]] : [];
      }

      console.log("Filtered result:", filtered);
      return filtered;
    }

    if (isManager) {
      if (managerListView === "my")
        return allLeaves.filter((l) => isSelfLeave(l));
      // "team" view: exclude own leaves so manager can't approve/reject themselves
      return allLeaves.filter((l) => !isSelfLeave(l));
    }
    return allLeaves;
  }, [
    allLeaves,
    isManager,
    managerListView,
    userId,
    notificationFilterActive,
    notificationLeaveId,
    scopedEmployeeId,
  ]);

  const leavesTableData = useMemo(() => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return [];

    return visibleLeaves.map((leave) => {
      const shouldShowActions = true;
      const selfLeave = isSelfLeave(leave);
      const canApproveReject =
        canGiveManagerApproval(leave) ||
        canGiveAdminApproval(leave) ||
        canRejectLeave(leave);
      const canEditOrDelete = canEditOrDeleteOwnLeave(leave);
      const isApproved = String(leave.status) === "2";
      const isRejected = String(leave.status) === "3";
      const isPending = String(leave.status) === "1";

      // Manager "My Leaves" view: add action column with view reason
      if (isManager && managerListView === "my") {
        const myLeaveActionNode = (
          <div className="action-menu-cell">
            <IconButton
              onClick={(e) => handleRowMenuOpen(e, leave)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
          </div>
        );

        return {
          employee_name: leave.employee_name || "N/A",
          applied_on: dateFormatter(leave.createdAt),
          leave_type: isEmergencyLeaveRecord(leave)
            ? formatEmergencyLeaveTypeDisplay(leave.leave_type)
            : formatLeaveTypeDisplay(leave.leave_type),
          start_date: dateFormatter(leave.start_date),
          end_date: dateFormatter(leave.end_date),
          total_days: leave.total_days || calculatedDays,
          available_leave: formatAvailableLeaveCell(leave),
          status: getStatusBadge(leave),
          action: myLeaveActionNode,
        };
      }

      // Action menu (3 dots) for approvals and for employees to view their own leave reasons
      const actionNode = !shouldShowActions ? null : canApproveReject ? (
        <div className="action-menu-cell">
          <IconButton onClick={(e) => handleRowMenuOpen(e, leave)} size="small">
            <MoreVertIcon />
          </IconButton>
        </div>
      ) : isApproved || isRejected || (isPending && selfLeave && isEmployee) ? (
        <div className="action-menu-cell">
          <IconButton onClick={(e) => handleRowMenuOpen(e, leave)} size="small">
            <MoreVertIcon />
          </IconButton>
        </div>
      ) : (
        <div className="action-menu-cell">
          {canEditOrDelete ? (
            <Button
              variant="outline"
              text="Delete"
              className="btn-delete"
              handleClick={() =>
                setDeleteModal({ open: true, leaveId: leave.leave_id })
              }
            />
          ) : null}
        </div>
      );

      return {
        employee_name: leave.employee_name || "N/A",
        applied_on: dateFormatter(leave.createdAt),
        leave_type: isEmergencyLeaveRecord(leave)
          ? formatEmergencyLeaveTypeDisplay(leave.leave_type)
          : formatLeaveTypeDisplay(leave.leave_type),
        start_date: dateFormatter(leave.start_date),
        end_date: dateFormatter(leave.end_date),
        total_days: leave.total_days || calculatedDays,
        available_leave: formatAvailableLeaveCell(leave),
        status: getStatusBadge(leave),
        action: actionNode,
      };
    });
  }, [
    visibleLeaves,
    isAdmin,
    isManager,
    managerListView,
    isEmployee,
    userId,
    calculatedDays,
  ]);

  const leavesTableHeaders = useMemo(() => {
    return [
      { id: "employee_name", label: "Name" },
      { id: "applied_on", label: "Applied On" },
      { id: "leave_type", label: "Leave Type" },
      { id: "start_date", label: "Start Date" },
      { id: "end_date", label: "End Date" },
      { id: "total_days", label: "Days" },
      { id: "available_leave", label: "Available Leave" },
      { id: "status", label: "Status" },
      { id: "action", label: "Action" },
    ];
  }, []);

  // Compute individual user statistics (for employees and managers viewing "My Leaves")
  // Exclude "Work From Home" from statistics calculations
  const userStatistics = useMemo(() => {
    if (!allLeaves || !Array.isArray(allLeaves)) return null;

    // Filter to get only current user's leaves, excluding Work From Home
    const myLeaves = allLeaves.filter(
      (l) =>
        isSelfLeave(l) &&
        String(l?.leave_type) !== "Work From Home" &&
        !isLegacyOtherLeaveType(l?.leave_type),
    );

    return {
      total_leaves: myLeaves.length,
      pending_leaves: myLeaves.filter((l) => String(l.status) === "1").length,
      approved_leaves: myLeaves.filter((l) => String(l.status) === "2").length,
      rejected_leaves: myLeaves.filter((l) => String(l.status) === "3").length,
    };
  }, [allLeaves, userId]);

  // Compute statistics based on visible leaves (for admin and manager team view)
  // Exclude "Work From Home" from statistics calculations
  const displayStatistics = useMemo(() => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return null;

    if (hideStatistics) return null;

    // Filter out Work From Home from statistics
    const leavesForStats = visibleLeaves.filter(
      (l) =>
        String(l?.leave_type) !== "Work From Home" &&
        !isLegacyOtherLeaveType(l?.leave_type),
    );

    const pending = leavesForStats.filter((l) => String(l.status) === "1");
    const approved = leavesForStats.filter((l) => String(l.status) === "2");
    const rejected = leavesForStats.filter((l) => String(l.status) === "3");

    return {
      total_leaves: leavesForStats.length,
      total_days: leavesForStats.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      pending_leaves: pending.length,
      pending_days: pending.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      approved_leaves: approved.length,
      approved_days: approved.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      rejected_leaves: rejected.length,
      rejected_days: rejected.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
    };
  }, [visibleLeaves, hideStatistics]);

  // Calculate WFH statistics based on visible leaves (for statistics section)
  const wfhStatistics = useMemo(() => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return null;

    if (hideStatistics) return null;

    // Filter only Work From Home leaves
    const wfhLeaves = visibleLeaves.filter(
      (l) => String(l?.leave_type) === "Work From Home",
    );

    const pending = wfhLeaves.filter((l) => String(l.status) === "1");
    const approved = wfhLeaves.filter((l) => String(l.status) === "2");
    const rejected = wfhLeaves.filter((l) => String(l.status) === "3");

    return {
      total_wfh: wfhLeaves.length,
      total_days: wfhLeaves.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      pending_wfh: pending.length,
      pending_days: pending.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      approved_wfh: approved.length,
      approved_days: approved.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      rejected_wfh: rejected.length,
      rejected_days: rejected.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
    };
  }, [visibleLeaves, hideStatistics]);

  // Calculate Comp Off statistics based on visible leaves (for statistics section)
  const compOffStatistics = useMemo(() => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return null;

    if (hideStatistics) return null;

    const compOffLeaves = visibleLeaves.filter(
      (l) => String(l?.leave_type || "").toLowerCase() === "comp off",
    );
    if (compOffLeaves.length === 0) return null;

    const pending = compOffLeaves.filter((l) => String(l.status) === "1");
    const approved = compOffLeaves.filter((l) => String(l.status) === "2");
    const rejected = compOffLeaves.filter((l) => String(l.status) === "3");

    return {
      total_leaves: compOffLeaves.length,
      total_days: compOffLeaves.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      pending_leaves: pending.length,
      pending_days: pending.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      approved_leaves: approved.length,
      approved_days: approved.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      rejected_leaves: rejected.length,
      rejected_days: rejected.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
    };
  }, [visibleLeaves]);

  const lopStatistics = useMemo(() => {
    if (!visibleLeaves || !Array.isArray(visibleLeaves)) return null;
    if (hideStatistics) return null;

    const lopLeaves = visibleLeaves.filter((l) =>
      isLopLeaveType(l?.leave_type),
    );
    if (lopLeaves.length === 0) return null;

    const pending = lopLeaves.filter((l) => String(l.status) === "1");
    const approved = lopLeaves.filter((l) => String(l.status) === "2");
    const rejected = lopLeaves.filter((l) => String(l.status) === "3");

    return {
      total_leaves: lopLeaves.length,
      total_days: lopLeaves.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      pending_leaves: pending.length,
      pending_days: pending.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      approved_leaves: approved.length,
      approved_days: approved.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
      rejected_leaves: rejected.length,
      rejected_days: rejected.reduce(
        (sum, l) => sum + Number(l?.total_days || 0),
        0,
      ),
    };
  }, [visibleLeaves, hideStatistics]);

  return (
    <div className="leaves-container">
      <div className="second-container">
        <div className="leaves-header">
          <div className="tabs">
            {!hideApplyTab && !isScopedEmployeeView && (
              <button
                className={`tab ${activeTab === "apply" ? "active" : ""}`}
                onClick={() => setActiveTab("apply")}
              >
                Apply Leave
              </button>
            )}
            <button
              className={`tab ${activeTab === "list" ? "active" : ""}`}
              onClick={() => setActiveTab("list")}
            >
              Leaves List
            </button>
            {/* {!isAdmin && (
              <button
                className={`tab ${activeTab === "balance" ? "active" : ""}`}
                onClick={() => setActiveTab("balance")}
              >
                Leave Balance
              </button>
            )} */}
          </div>
        </div>

        {/* Apply Leave Tab */}
        {!hideApplyTab && !isScopedEmployeeView && activeTab === "apply" && (
          <div className="leave-form-section">
            {/* Leave Balance Display */}
            <div className="balance-cards-grid">
              {displayedLeaveBalance.map((bal) => (
                <div key={bal.leave_type} className="balance-card">
                  <h3>{bal.leave_type}</h3>
                  <div className="balance-details">
                    <div className="balance-item">
                      <span className="label">Total:</span>
                      <span className="value">{bal.total_leaves || 0}</span>
                    </div>
                    <div className="balance-item">
                      <span className="label">Used:</span>
                      <span className="value">{bal.used_leaves || 0}</span>
                    </div>
                    <div className="balance-item available">
                      <span className="label">Available:</span>
                      <span className="value">{bal.balance || 0}</span>
                    </div>
                    {bal.leave_type === "Casual" && (
                      <small className="calculation-note">
                        (1 per month, no carry forward)
                      </small>
                    )}
                    {bal.leave_type === "Sick" && (
                      <small className="calculation-note">
                        (6 per calendar year, no carry forward)
                      </small>
                    )}
                  </div>
                </div>
              ))}
              {canShowWorkFromHome ? (
                <div className="balance-card">
                  <h3>Work From Home</h3>
                  <div className="balance-details">
                    <div className="balance-item available">
                      <span className="label">Taken (Approved):</span>
                      <span className="value">{wfhTakenDays} day(s)</span>
                    </div>
                    <small className="calculation-note">
                      (Not counted as leave)
                    </small>
                  </div>
                </div>
              ) : null}
              {/* LOP & Emergency Card */}
              <div className="balance-card">
                <div>
                  <h3>LOP</h3>
                  <div className="balance-details">
                    <div className="balance-item">
                      <span className="label">Taken (Approved):</span>
                      <span className="value">{lopTakenDays} day(s)</span>
                    </div>
                    {/* <small className="calculation-note">
                      (Loss of Pay — no available balance)
                    </small> */}
                  </div>
                </div>

                <div
                  style={{ borderTop: "2px solid #1976d2", margin: "12px 0" }}
                />

                <div>
                  <div>
                    <h3 style={{ marginBottom: "2px" }}>Emergency Leave</h3>
                    <small style={{ display: "block", color: "#64748b", fontSize: "0.75rem", marginBottom: "12px" }}>
                      (Deducted from casual leave)
                    </small>
                  </div>

                  <div className="balance-details">
                    <div className="balance-item">
                      <span className="label">Available:</span>
                      <span className="value">
                        {emergencyLeaveBalance} day(s)
                      </span>
                    </div>
                    <small className="calculation-note">
                      (1 per calendar month)
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(false, e);
              }}
            >
              <div
                className="form-row date-row"
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-end",
                  marginBottom: "20px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Labelbox
                    type="select"
                    labelname="Leave Duration"
                    value={formData.day_type}
                    changeData={(value) => handleFormChange("day_type", value)}
                    dropdown={[
                      { id: "full", value: "Full Day" },
                      { id: "half", value: "Half Day" },
                      { id: "custom", value: "Custom" },
                    ]}
                    required
                  />
                </div>

                {formData.day_type === "custom" && (
                  <div style={{ flex: 1 }}>
                    <Labelbox
                      type="text"
                      labelname="Custom Days"
                      value={formData.custom_days}
                      changeData={(value) =>
                        handleFormChange("custom_days", value)
                      }
                      placeholder="e.g. 1 or 1.5"
                      required
                    />
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <Labelbox
                    type="select"
                    labelname="Leave Type"
                    value={formData.leave_type}
                    changeData={(value) => {
                      if (value && !isUnlimitedBalanceLeaveType(value)) {
                        const available = getAvailableBalance(value);
                        if (available === 0) {
                          const displayType =
                            value === "Emergency Leave"
                              ? "Comp Off or Casual leave"
                              : `${value} leave`;
                          Toaster.error(
                            `You have 0 available balance for ${displayType}. Please select other available leave`,
                          );
                          return;
                        }
                      }

                      if (value === "Work From Home") {
                        const now = new Date();
                        if (
                          hasWorkFromHomeInMonth(
                            now.getFullYear(),
                            now.getMonth(),
                          )
                        ) {
                          Toaster.warning(getWfhAlreadyTakenMessage(now));
                        }
                      }

                      if (value === "Emergency Leave") {
                        const now = new Date();
                        if (
                          hasEmergencyLeaveInMonth(
                            now.getFullYear(),
                            now.getMonth(),
                          )
                        ) {
                          Toaster.warning(getEmergencyAlreadyTakenMessage(now));
                          return;
                        }
                      }

                      // Reset dates when leave type changes to ensure proper validation
                      setFormData((prev) => ({
                        ...prev,
                        leave_type: value,
                        start_date: "",
                        end_date: "",
                        is_emergency: value === "Emergency Leave",
                      }));
                      setCalculatedDays(0);
                    }}
                    dropdown={leaveTypeOptions}
                    required
                  />
                </div>
              </div>

              <div
                className="form-row date-row"
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-end",
                  marginBottom: "20px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <Labelbox
                    type="datepicker"
                    labelname="Start Date"
                    value={formData.start_date}
                    changeData={(value) => {
                      if (formData.is_emergency && value) {
                        const start = parseLeaveDateOnly(value);
                        if (
                          start &&
                          hasEmergencyLeaveInMonth(
                            start.getFullYear(),
                            start.getMonth(),
                          )
                        ) {
                          Toaster.warning(
                            getEmergencyAlreadyTakenMessage(value),
                          );
                          return;
                        }
                        // Emergency leave is always a single day — end date matches start date
                        setFormData((prev) => ({
                          ...prev,
                          start_date: value,
                          end_date: value,
                        }));
                        return;
                      }

                      if (formData.leave_type === "Work From Home" && value) {
                        const start = parseLeaveDateOnly(value);
                        if (
                          start &&
                          hasWorkFromHomeInMonth(
                            start.getFullYear(),
                            start.getMonth(),
                          )
                        ) {
                          Toaster.warning(getWfhAlreadyTakenMessage(value));
                          return;
                        }
                        // WFH is always a single day — end date matches start date
                        setFormData((prev) => ({
                          ...prev,
                          start_date: value,
                          end_date: value,
                        }));
                        return;
                      }

                      if (
                        formData.day_type === "custom" &&
                        value &&
                        isValidCustomLeaveDays(formData.custom_days)
                      ) {
                        setFormData((prev) => ({
                          ...prev,
                          start_date: value,
                          end_date: getEndDateYmdFromCustomDays(
                            value,
                            prev.custom_days,
                          ),
                        }));
                        return;
                      }

                      handleFormChange("start_date", value);
                      // Reset end date if it becomes invalid after start date change
                      if (formData.end_date && value) {
                        const start = new Date(value);
                        const end = new Date(formData.end_date);
                        if (end < start) {
                          handleFormChange("end_date", "");
                        }
                      }
                    }}
                    required
                    shouldDisableDate={shouldDisableLeaveDate}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <Labelbox
                    type="datepicker"
                    labelname="End Date"
                    value={formData.end_date}
                    changeData={(value) => {
                      // WFH / Custom / Emergency end date is auto-managed
                      if (
                        formData.leave_type === "Work From Home" ||
                        formData.is_emergency ||
                        formData.day_type === "custom"
                      ) {
                        return;
                      }

                      // Validate against available balance (effective days for half-day)
                      if (formData.start_date && value) {
                        const start = parseYmdToLocalDate(formData.start_date);
                        const end = parseYmdToLocalDate(value);
                        const days = countWeekdaysInclusive(start, end);

                        const available = getAvailableBalance(
                          formData.leave_type,
                        );
                        if (available !== "Unlimited") {
                          const effectiveDaysNeeded =
                            formData.day_type === "half" ? days * 0.5 : days;
                          if (
                            Number(available) > 0 &&
                            effectiveDaysNeeded > Number(available) + 1e-8
                          ) {
                            Toaster.error(
                              `You can only select up to ${available} ${formData.leave_type} leave(s). Please select a date within your available balance.`,
                            );
                            return; // Don't update if exceeds balance
                          }
                        }
                      }
                      handleFormChange("end_date", value);
                    }}
                    required
                    min={formData.start_date}
                    max={
                      formData.leave_type === "Work From Home" ||
                      formData.is_emergency ||
                      formData.day_type === "custom"
                        ? formData.end_date || formData.start_date
                        : getMaxEndDate()
                    }
                    disabled={
                      formData.leave_type === "Work From Home" ||
                      formData.is_emergency ||
                      formData.day_type === "custom"
                    }
                    shouldDisableDate={shouldDisableLeaveDate}
                  />
                </div>
              </div>

              {formData.start_date &&
                formData.leave_type !== "Work From Home" &&
                formData.leave_type !== "LOP" &&
                formData.leave_type !== "Emergency Leave" && (
                  <div className="date-range-hint">
                    <small>
                      You can select up to{" "}
                      <strong>
                        {getAvailableBalance(formData.leave_type)}
                      </strong>{" "}
                      days
                      {/* {getMaxEndDate() &&
                        (() => {
                          const dateStr = getMaxEndDate();
                          const parts = dateStr.split("-");
                          const year = parseInt(parts[0], 10);
                          const month = parseInt(parts[1], 10);
                          const day = parseInt(parts[2], 10);
                          const date = new Date(year, month - 1, day);
                          return ` (until ${date.toLocaleDateString()})`;
                        })()} */}
                    </small>
                  </div>
                )}

              {(calculatedDays > 0 ||
                (formData.day_type === "custom" &&
                  isValidCustomLeaveDays(formData.custom_days))) && (
                <div className="calculated-days">
                  <strong>
                    Total Days:{" "}
                    {formData.day_type === "custom"
                      ? effectiveDays
                      : formData.day_type === "half"
                        ? `${effectiveDays} (half-day)`
                        : calculatedDays}
                  </strong>
                  {formData.leave_type !== "Work From Home" &&
                    formData.leave_type !== "LOP" && (
                      <div className="balance-info-text">
                        Available: {getAvailableBalance(formData.leave_type)} |
                        Remaining:{" "}
                        {getAvailableBalance(formData.leave_type) -
                          effectiveDays}
                      </div>
                    )}
                </div>
              )}

              <div className="form-row" style={{ marginBottom: "20px" }}>
                <textarea
                  className="comments-textarea"
                  value={formData.reason}
                  onChange={(e) => handleFormChange("reason", e.target.value)}
                  placeholder="Enter reason for leave..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-row">
                <div>
                  {" "}
                  <Labelbox
                    type="text"
                    labelname="Notification Emails"
                    value={formData.notification_emails}
                    changeData={(value) =>
                      handleFormChange("notification_emails", value)
                    }
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <small style={{ marginTop: 8 }}>
                    Comma-separated list of emails to <strong>cc</strong>{" "}
                    (optional)
                  </small>
                </div>

                <small style={{ marginTop: 0, marginLeft: "1rem" }}>
                  <strong>Note:</strong> hr@finlotax.com,{" "}
                  shine_kurian@finlotax.com and praveen.j@finlotax.com will be automatically included in{" "}
                  <strong>cc</strong>.
                </small>
              </div>

              {/* <div className="form-row" style={{ marginTop: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input
                    type="checkbox"
                    id="is_emergency"
                    checked={
                      !isLopLeaveType(formData.leave_type) &&
                      formData.is_emergency
                    }
                    disabled={isLopLeaveType(formData.leave_type)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (isLopLeaveType(formData.leave_type)) {
                        Toaster.warning(
                          "LOP cannot be applied as emergency leave.",
                        );
                        return;
                      }
                      if (checked) {
                        const start = parseLeaveDateOnly(formData.start_date);
                        if (
                          start &&
                          hasEmergencyLeaveInMonth(
                            start.getFullYear(),
                            start.getMonth(),
                          )
                        ) {
                          Toaster.warning(
                            getEmergencyAlreadyTakenMessage(start),
                          );
                          return;
                        }

                        // Determine if current selection spans more than 1 day
                        const currentDaysNeeded =
                          formData.day_type === "custom"
                            ? Number(formData.custom_days || 0)
                            : formData.start_date && formData.end_date
                                ? countWeekdaysInclusive(
                                    parseYmdToLocalDate(formData.start_date),
                                    parseYmdToLocalDate(formData.end_date),
                                  )
                                : 0);

                        if (currentDaysNeeded > 1) {
                          setFormData((prev) => ({
                            ...prev,
                            is_emergency: true,
                            custom_days: prev.day_type === "custom" ? "1" : "",
                            start_date: "",
                            end_date: "",
                          }));
                          setCalculatedDays(0);
                          Toaster.warning(
                            "Emergency leave cannot exceed 1 day. Dates have been reset.",
                          );
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            is_emergency: true,
                            custom_days: prev.day_type === "custom" ? (prev.custom_days || "1") : "",
                            end_date: prev.start_date || "",
                          }));
                        }
                      } else {
                        handleFormChange("is_emergency", false);
                      }
                    }}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: isLopLeaveType(formData.leave_type)
                        ? "not-allowed"
                        : "pointer",
                      margin: 0,
                    }}
                  />
                  <label
                    htmlFor="is_emergency"
                    style={{
                      cursor: isLopLeaveType(formData.leave_type)
                        ? "not-allowed"
                        : "pointer",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      margin: 0,
                      opacity: isLopLeaveType(formData.leave_type) ? 0.55 : 1,
                    }}
                  >
                    Emergency Leave
                  </label>
                </div>
              </div> */}

              <div className="form-actions">
                <Button
                  text={
                    loading
                      ? "Submitting..."
                      : editingLeaveId
                        ? "Update Leave"
                        : "Submit for Approval"
                  }
                  disable={loading}
                  handleClick={(e) => handleSubmit(false, e)}
                />
                {editingLeaveId ? (
                  <Button
                    variant="outline"
                    text="Cancel"
                    disable={loading}
                    handleClick={() => {
                      setEditingLeaveId(null);
                      setFormData({
                        leave_type: "",
                        day_type: "",
                        custom_days: "",
                        start_date: "",
                        end_date: "",
                        reason: "",
                        notification_emails: managerEmailRef.current || "",
                        is_emergency: false,
                        status: 1,
                      });
                      setCalculatedDays(0);
                    }}
                  />
                ) : null}
                {/* <Button
                  variant="outline"
                  text="Save as Draft"
                  disable={loading}
                  handleClick={(e) => handleSubmit(true, e)}
                /> */}
              </div>
            </form>
          </div>
        )}

        {/* Leaves List Tab */}
        {activeTab === "list" && (
          <div className="leaves-list-section">
            {isAdmin && !isScopedEmployeeView && (
              <div className="admin-comp-off-bar">
                <Button
                  text="Add Comp Off"
                  disable={loading}
                  handleClick={openCompOffModal}
                />
              </div>
            )}

            {isManager && !isScopedEmployeeView && (
              <div className="manager-list-toggle">
                <button
                  className={`toggle-btn ${managerListView === "team" ? "active" : ""}`}
                  onClick={() => setManagerListView("team")}
                >
                  Team Leaves
                </button>
                <button
                  className={`toggle-btn ${managerListView === "my" ? "active" : ""}`}
                  onClick={() => setManagerListView("my")}
                >
                  My Leaves
                </button>
              </div>
            )}

            {readOnlyView && isScopedEmployeeView && (
              <div className="available-leaves-section">
                <h3>Available Leaves</h3>
                <div className="balance-cards-grid">
                  {(displayedLeaveBalance || []).map((bal) => (
                    <div key={bal.leave_type} className="balance-card">
                      <h3>{bal.leave_type}</h3>
                      <div className="balance-details">
                        <div className="balance-item">
                          <span className="label">Total:</span>
                          <span className="value">{bal.total_leaves || 0}</span>
                        </div>
                        <div className="balance-item">
                          <span className="label">Used:</span>
                          <span className="value">{bal.used_leaves || 0}</span>
                        </div>
                        <div className="balance-item available">
                          <span className="label">Available:</span>
                          <span className="value">{bal.balance || 0}</span>
                        </div>
                        {bal.leave_type === "Casual" && (
                          <small className="calculation-note">
                            (1 per month, no carry forward)
                          </small>
                        )}
                        {bal.leave_type === "Sick" && (
                          <small className="calculation-note">
                            (6 per calendar year, no carry forward)
                          </small>
                        )}
                      </div>
                    </div>
                  ))}

                  {canShowWorkFromHome ? (
                    <div className="balance-card">
                      <h3>Work From Home</h3>
                      <div className="balance-details">
                        <div className="balance-item available">
                          <span className="label">Taken (Approved):</span>
                          <span className="value">{wfhTakenDays} day(s)</span>
                        </div>
                        <small className="calculation-note">
                          (Not counted as leave)
                        </small>
                      </div>
                    </div>
                  ) : null}
                  <div className="balance-card">
                    <div>
                      <h3>LOP</h3>
                      <div className="balance-details">
                        <div className="balance-item available">
                          <span className="label">Taken (Approved):</span>
                          <span className="value">{lopTakenDays} day(s)</span>
                        </div>
                        <small className="calculation-note">
                          (Loss of Pay — no available balance)
                        </small>
                      </div>
                    </div>

                    <div
                      style={{
                        borderTop: "1px solid #e2e8f0",
                        margin: "12px 0",
                      }}
                    />

                    <div>
                      <h3 style={{ marginBottom: "2px" }}>Emergency Leave</h3>
                      <small style={{ display: "block", color: "#64748b", fontSize: "0.75rem", marginBottom: "12px" }}>
                        (Deducted from casual leave)
                      </small>
                      <div className="balance-details">
                        <div className="balance-item available">
                          <span className="label">Available:</span>
                          <span className="value">
                            {emergencyLeaveBalance} day(s)
                          </span>
                        </div>
                        <small className="calculation-note">
                          (Max 1 per calendar month)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {(displayStatistics ||
              compOffStatistics ||
              wfhStatistics ||
              lopStatistics) && (
              <div className="statistics-section">
                <h3>Leave Statistics</h3>
                <div className="unified-stats-card">
                  {/* Leaves Statistics */}
                  {displayStatistics && (
                    <div className="stats-group">
                      <h4 className="stats-group-title">Leaves</h4>
                      <div className="stats-row">
                        <span className="stats-label">Total Applies:</span>
                        <span className="stats-value">
                          {displayStatistics.total_leaves || 0}{" "}
                          <span className="stats-days">
                            ({displayStatistics.total_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Pending:</span>
                        <span className="stats-value">
                          {displayStatistics.pending_leaves || 0}{" "}
                          <span className="stats-days">
                            ({displayStatistics.pending_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Approved:</span>
                        <span className="stats-value">
                          {displayStatistics.approved_leaves || 0}{" "}
                          <span className="stats-days">
                            ({displayStatistics.approved_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Rejected:</span>
                        <span className="stats-value">
                          {displayStatistics.rejected_leaves || 0}{" "}
                          <span className="stats-days">
                            ({displayStatistics.rejected_days || 0} days)
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Comp Off Statistics */}
                  {compOffStatistics && (
                    <div className="stats-group">
                      <h4 className="stats-group-title">Comp Off</h4>
                      <div className="stats-row">
                        <span className="stats-label">Total Applies:</span>
                        <span className="stats-value">
                          {compOffStatistics.total_leaves || 0}{" "}
                          <span className="stats-days">
                            ({compOffStatistics.total_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Pending:</span>
                        <span className="stats-value">
                          {compOffStatistics.pending_leaves || 0}{" "}
                          <span className="stats-days">
                            ({compOffStatistics.pending_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Approved:</span>
                        <span className="stats-value">
                          {compOffStatistics.approved_leaves || 0}{" "}
                          <span className="stats-days">
                            ({compOffStatistics.approved_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Rejected:</span>
                        <span className="stats-value">
                          {compOffStatistics.rejected_leaves || 0}{" "}
                          <span className="stats-days">
                            ({compOffStatistics.rejected_days || 0} days)
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Work From Home Statistics */}
                  {wfhStatistics && (
                    <div className="stats-group">
                      <h4 className="stats-group-title">
                        Work From Home
                        <small
                          className="calculation-note"
                          style={{
                            marginLeft: "8px",
                            fontSize: "0.7rem",
                            fontWeight: "normal",
                            color: "#666",
                          }}
                        >
                          (Not counted as leave)
                        </small>
                      </h4>
                      <div className="stats-row">
                        <span className="stats-label">Total Applies:</span>
                        <span className="stats-value">
                          {wfhStatistics.total_wfh || 0}{" "}
                          <span className="stats-days">
                            ({wfhStatistics.total_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Pending:</span>
                        <span className="stats-value">
                          {wfhStatistics.pending_wfh || 0}{" "}
                          <span className="stats-days">
                            ({wfhStatistics.pending_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Approved:</span>
                        <span className="stats-value">
                          {wfhStatistics.approved_wfh || 0}{" "}
                          <span className="stats-days">
                            ({wfhStatistics.approved_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Rejected:</span>
                        <span className="stats-value">
                          {wfhStatistics.rejected_wfh || 0}{" "}
                          <span className="stats-days">
                            ({wfhStatistics.rejected_days || 0} days)
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {lopStatistics && (
                    <div className="stats-group">
                      <h4 className="stats-group-title">LOP</h4>
                      <div className="stats-row">
                        <span className="stats-label">Total Applies:</span>
                        <span className="stats-value">
                          {lopStatistics.total_leaves || 0}{" "}
                          <span className="stats-days">
                            ({lopStatistics.total_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Pending:</span>
                        <span className="stats-value">
                          {lopStatistics.pending_leaves || 0}{" "}
                          <span className="stats-days">
                            ({lopStatistics.pending_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Approved:</span>
                        <span className="stats-value">
                          {lopStatistics.approved_leaves || 0}{" "}
                          <span className="stats-days">
                            ({lopStatistics.approved_days || 0} days)
                          </span>
                        </span>
                      </div>
                      <div className="stats-row">
                        <span className="stats-label">Rejected:</span>
                        <span className="stats-value">
                          {lopStatistics.rejected_leaves || 0}{" "}
                          <span className="stats-days">
                            ({lopStatistics.rejected_days || 0} days)
                          </span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {!isScopedEmployeeView && !hideFilters && (
              <>
                <div
                  style={{
                    justifyContent: "flex-end",
                    display: "flex",
                    marginBottom: "20px",
                    // marginTop: "28px",
                    gap: "12px",
                  }}
                >
                  <Button
                    // style={{ paddingTop: 22 }}
                    // variant="outline"
                    text="Clear Filters"
                    handleClick={() =>
                      setFilters({
                        start_date: "",
                        end_date: "",
                        leave_type: "",
                        status: "",
                        employee_id: "all",
                      })
                    }
                  />
                  {/* {filters.employee_id && filters.employee_id !== "all" && (
                    <Button
                      // variant="outline"
                      text="Export to Excel"
                      handleClick={handleExportLeavesToExcel}
                    />
                  )} */}
                </div>

                <div className="filters-section">
                  {(isAdmin || (isManager && managerListView === "team")) && (
                    <>
                      <Labelbox
                        type="select"
                        labelname="Employee"
                        value={filters.employee_id}
                        changeData={(value) =>
                          handleFilterChange("employee_id", value)
                        }
                        dropdown={leaveListEmployeeOptions}
                      />
                    </>
                  )}
                  <Labelbox
                    type="datepicker"
                    labelname="Start Date"
                    value={filters.start_date}
                    changeData={(value) =>
                      handleFilterChange("start_date", value)
                    }
                  />
                  <Labelbox
                    type="datepicker"
                    labelname="End Date"
                    value={filters.end_date}
                    changeData={(value) =>
                      handleFilterChange("end_date", value)
                    }
                  />
                  <Labelbox
                    type="select"
                    labelname="Leave Type"
                    value={filters.leave_type}
                    changeData={(value) =>
                      handleFilterChange("leave_type", value)
                    }
                    dropdown={[
                      { id: "", value: "All Leave Types" },
                      { id: "Sick", value: "Sick" },
                      { id: "Casual", value: "Casual" },
                      { id: "Work From Home", value: "Work From Home" },
                      { id: "Comp Off", value: "Comp Off" },
                      { id: "LOP", value: "LOP" },
                      { id: "Dummy Leaves", value: "Dummy Leaves" },
                    ]}
                  />
                  <Labelbox
                    type="select"
                    labelname="Status"
                    value={filters.status}
                    changeData={(value) => handleFilterChange("status", value)}
                    dropdown={[
                      { id: "", value: "All Status" },
                      // { id: "0", value: "Saved" },
                      { id: "1", value: "Pending" },
                      { id: "2", value: "Approved" },
                      { id: "3", value: "Rejected" },
                    ]}
                  />
                </div>
              </>
            )}

            <Table
              header={leavesTableHeaders}
              data={leavesTableData || []}
              aligncss="dashboard-table employee-table"
              className="employee-table-scope"
              variant="small"
            />

            {/* Row action menu (3 dots) */}
            <Menu
              anchorEl={rowMenu.anchorEl}
              open={Boolean(rowMenu.anchorEl)}
              onClose={handleRowMenuClose}
            >
              <MenuItem
                onClick={() => {
                  const leave = rowMenu.leave;
                  handleRowMenuClose();
                  setViewReasonModal({
                    open: true,
                    reason: leave?.reason || "No reason provided",
                    rejectedReason: leave?.rejected_comments || "",
                    isRejection: String(leave?.status) === "3",
                  });
                }}
              >
                View Reason
              </MenuItem>
              {(canGiveManagerApproval(rowMenu.leave) ||
                canGiveAdminApproval(rowMenu.leave)) && (
                <MenuItem
                  onClick={() => {
                    const leave = rowMenu.leave;
                    handleRowMenuClose();
                    openConfirmModal("approve", leave);
                  }}
                >
                  Approve
                </MenuItem>
              )}
              {canRejectLeave(rowMenu.leave) &&
                !(isManager && managerListView === "my") && (
                  <MenuItem
                    onClick={() => {
                      const leave = rowMenu.leave;
                      handleRowMenuClose();
                      openConfirmModal("reject", leave);
                    }}
                  >
                    Reject
                  </MenuItem>
                )}
              {canEditOrDeleteOwnLeave(rowMenu.leave) && (
                <MenuItem
                  onClick={() => {
                    const leave = rowMenu.leave;
                    handleRowMenuClose();
                    openLeaveEditor(leave);
                  }}
                >
                  Edit
                </MenuItem>
              )}
              {canEditOrDeleteOwnLeave(rowMenu.leave) && (
                <MenuItem
                  onClick={() => {
                    const leave = rowMenu.leave;
                    handleRowMenuClose();
                    setDeleteModal({ open: true, leaveId: leave.leave_id });
                  }}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </div>
        )}

        {/* Leave Balance Tab */}
        {/* {!isAdmin && activeTab === "balance" && (
          <div className="leave-balance-section">
            <div className="balance-cards-grid">
              {displayedLeaveBalance.map((bal) => (
                <div key={bal.leave_type} className="balance-card">
                  <h3>{bal.leave_type}</h3>
                  <div className="balance-details">
                    <div className="balance-item">
                      <span className="label">Total:</span>
                      <span className="value">{bal.total_leaves || 0}</span>
                    </div>
                    <div className="balance-item">
                      <span className="label">Used:</span>
                      <span className="value">{bal.used_leaves || 0}</span>
                    </div>
                    <div className="balance-item available">
                      <span className="label">Available:</span>
                      <span className="value">{bal.balance || 0}</span>
                    </div>
                    {bal.leave_type === "Casual" && (
                      <small className="calculation-note">
                        (Current Month: {new Date().getMonth() + 1} - Used:{" "}
                        {bal.used_leaves || 0})
                      </small>
                    )}
                  </div>
                </div>
              ))}

              <div className="balance-card">
                <h3>Work From Home</h3>
                <div className="balance-details">
                  <div className="balance-item available">
                    <span className="label">Taken (Approved):</span>
                    <span className="value">{wfhTakenDays} day(s)</span>
                  </div>
                  <small className="calculation-note">
                    (Not counted as leave)
                  </small>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* View Reason Modal */}
        {viewReasonModal.open && (
          <div
            className="logout-modal-overlay"
            onClick={() => {
              setViewReasonModal({
                open: false,
                reason: "",
                rejectedReason: "",
                isRejection: false,
              });
            }}
          >
            <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="logout-modal-title">
                {viewReasonModal.isRejection ? "Reason" : "Leave Reason"}
              </h2>
              <div className="form-row" style={{ marginTop: 12 }}>
                {viewReasonModal.isRejection && (
                  <label
                    style={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#1C74BC",
                      // marginBottom: "0.5rem",
                      display: "block",
                    }}
                  >
                    Leave Reason
                  </label>
                )}
                <textarea
                  className="comments-textarea"
                  value={viewReasonModal.reason}
                  readOnly
                  rows={viewReasonModal.isRejection ? 3 : 5}
                  style={{ backgroundColor: "#f5f5f5", cursor: "default" }}
                />
              </div>

              {viewReasonModal.isRejection && (
                <div className="form-row" style={{ marginTop: 12 }}>
                  <label
                    style={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#1C74BC",
                      // marginBottom: "0.5rem",
                      display: "block",
                    }}
                  >
                    Rejected Reason
                  </label>
                  <textarea
                    className="comments-textarea"
                    value={viewReasonModal.rejectedReason}
                    readOnly
                    rows={3}
                    style={{ backgroundColor: "#f5f5f5", cursor: "default" }}
                  />
                </div>
              )}

              <div className="logout-modal-buttons">
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={() => {
                    setViewReasonModal({
                      open: false,
                      reason: "",
                      rejectedReason: "",
                      isRejection: false,
                    });
                  }}
                  style={{
                    backgroundColor: "var(--Primary-color)",
                    marginTop: 12,
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approve/Reject Modal */}
        {/* Approve/Reject Confirmation Modal (same style as logout modal) */}
        {approveRejectModal.open && (
          <div
            className="logout-modal-overlay"
            onClick={() => {
              setApproveRejectModal({
                open: false,
                type: "",
                leaveId: null,
                leaveData: null,
              });
              setRejectionComments("");
            }}
          >
            <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="logout-modal-title">
                {approveRejectModal.type === "approve"
                  ? "Approve Leave"
                  : "Reject Leave"}
              </h2>
              <p className="logout-modal-question">
                {approveRejectModal.type === "approve"
                  ? "Are you sure you want to approve this leave? It is approved only after both the manager and an admin have approved."
                  : "Are you sure you want to reject this leave?"}
              </p>

              {approveRejectModal.type === "reject" && (
                <div className="form-row" style={{ marginTop: 12 }}>
                  <textarea
                    className="comments-textarea"
                    value={rejectionComments}
                    onChange={(e) => setRejectionComments(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    rows={3}
                  />
                </div>
              )}

              <div className="logout-modal-buttons">
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-no"
                  onClick={() => {
                    setApproveRejectModal({
                      open: false,
                      type: "",
                      leaveId: null,
                      leaveData: null,
                    });
                    setRejectionComments("");
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={() => {
                    if (approveRejectModal.type === "approve") {
                      handleApprove();
                    } else {
                      handleReject();
                    }
                  }}
                  style={{
                    backgroundColor:
                      approveRejectModal.type === "approve"
                        ? "var(--Primary-color)"
                        : "#f44336",
                  }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Leave Confirmation Modal */}
        {deleteModal.open && (
          <div
            className="logout-modal-overlay"
            onClick={() => setDeleteModal({ open: false, leaveId: null })}
          >
            <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="logout-modal-title">Delete Leave</h2>
              <p className="logout-modal-question">
                Are you sure you want to delete this leave? This action cannot
                be undone.
              </p>
              <div className="logout-modal-buttons">
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-no"
                  onClick={() => setDeleteModal({ open: false, leaveId: null })}
                >
                  No
                </button>
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={() => handleDelete(deleteModal.leaveId)}
                  style={{ backgroundColor: "#f44336" }}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin: Add Comp Off Modal (logout-style) */}
        {compOffModalOpen && (
          <div className="logout-modal-overlay" onClick={closeCompOffModal}>
            <div
              className="logout-modal comp-off-modal"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "900px", width: "90%" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <h2 className="logout-modal-title" style={{ margin: 0 }}>
                  Add Comp Off
                </h2>
                <IconButton
                  onClick={closeCompOffModal}
                  aria-label="close"
                  style={{ padding: "8px" }}
                >
                  <CloseIcon />
                </IconButton>
              </div>

              <div className="comp-off-modal-body">
                <div className="comp-off-grid">
                  <div className="comp-off-left">
                    <div>
                      <label
                        style={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: "#1C74BC",
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        All
                      </label>
                      <Autocomplete
                        multiple
                        disablePortal
                        disableCloseOnSelect
                        options={compOffEmployeeOptions}
                        value={compOffSelectedOptions}
                        onChange={(_, newValue) => {
                          // newValue is an array of option objects from MUI
                          if (!Array.isArray(newValue)) {
                            setCompOffSelectedOptions([]);
                            return;
                          }
                          // Deduplicate by employee_id to prevent selecting same employee twice
                          const seen = new Set();
                          const unique = newValue.filter((opt) => {
                            const id = String(opt?.value ?? "").trim();
                            if (!id) return false;
                            if (seen.has(id)) return false;
                            seen.add(id);
                            return true;
                          });
                          setCompOffSelectedOptions(unique);
                        }}
                        isOptionEqualToValue={(opt, val) => {
                          // Critical: Compare by employee_id value, not object reference
                          return (
                            String(opt?.value ?? "").trim() ===
                            String(val?.value ?? "").trim()
                          );
                        }}
                        getOptionLabel={(opt) => String(opt?.label || "")}
                        renderOption={(props, option) => {
                          const isSelected = compOffSelectedIdSet.has(
                            String(option?.value ?? "").trim(),
                          );
                          return (
                            <li {...props} style={{ padding: "6px 16px" }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                style={{
                                  marginRight: "8px",
                                  cursor: "pointer",
                                }}
                              />
                              <span>{option?.label}</span>
                            </li>
                          );
                        }}
                        renderTags={() => null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="All"
                            placeholder="Search..."
                          />
                        )}
                      />
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <label
                        style={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: "#1C74BC",
                          display: "block",
                          marginBottom: 6,
                        }}
                      >
                        Selected Employees
                      </label>
                      <div className="selected-employees-box">
                        {compOffSelectedOptions.length === 0 ? (
                          <span className="selected-employees-empty">
                            No employees selected
                          </span>
                        ) : (
                          compOffSelectedOptions.map((opt) => (
                            <div
                              key={String(opt?.value ?? opt?.label)}
                              className="selected-employees-row"
                            >
                              {opt?.label}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div style={{ marginTop: 16 }}>
                      <Labelbox
                        type="number"
                        labelname="Comp Off Days"
                        value={compOffDays}
                        changeData={(v) => setCompOffDays(v)}
                      />
                    </div>
                  </div>

                  <div className="comp-off-right">
                    <label
                      style={{
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "#1C74BC",
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      Description (email content)
                    </label>
                    <textarea
                      className="comments-textarea comp-off-description"
                      value={compOffDescription}
                      onChange={(e) => setCompOffDescription(e.target.value)}
                      placeholder="Enter description to send to selected employee(s)..."
                      rows={14}
                    />
                  </div>
                </div>
              </div>

              <div
                className="logout-modal-buttons"
                style={{ marginTop: "20px" }}
              >
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-no"
                  onClick={closeCompOffModal}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={submitCompOff}
                  disabled={loading}
                  style={{ backgroundColor: "var(--Primary-color)" }}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaves;
